import { BusinessSector } from "@enums/businessSector.enum";
import { Currency } from "@enums/currency.enum";
import { Incoterms } from "@enums/incoterms.enum";
import { MaterialQuoting } from "@enums/materialQuoting.enum";
import { PackagingTerms } from "@enums/packagingTerms.enum";
import { BoughtOutPartsLineData } from "@interfaces/boughtOutPartsLineData.interface";
import { Model } from "@interfaces/model.interface";
import { OutsourcedProcessLineData } from "@interfaces/outsourcedProcessLineData.interface";
import { PressProcessLineData } from "@interfaces/pressProcessLineData.interface";
import { RawMaterialLineData } from "@interfaces/rawMaterialLineData.interface";
import { ToolingCostLineData } from "@interfaces/toolingCostLineData.interface";
import { ToolingFixtureLineData } from "@interfaces/toolingFixtureLineData.interface";

export interface QuotationAnalysisForm extends Model {
	// --- Financial summary calculations ---
	additionalCostsSubtotal: number;
	additionalCostsSubtotalDisplayToCustomer: boolean;
	additionalNotes: string;
	// --- Admin, engineering and quality: hours x hourly rate, amortised into the piece price by default ---
	adminAmortised: boolean;
	adminCost: number;
	adminHours: number;
	adminRate: number;
	annualCost: number;
	annualPrice: number;
	annualValueAdded: number;
	annualVolume: number;
	approved: boolean;
	approvedById? : number;
	approvedOn?: Date;
	approvedRateOverrides?: string; // JSON, the rate changes as approved
	batchCost: number;
	batchPrice: number;
	batchSize: number;
	batchValueAdded: number;
	// --- Bought out ---
	boughtOutMarginCost: number;
	boughtOutMarginPercentage: number;
	boughtOutPartsDisplayToCustomer: boolean;
	boughtOutPartsDocumentsLineData?: string; // JSON format, SupportingDocumentFileLineData
	boughtOutPartsLineData: string; // JSON format, BoughtOutPartsLineData
	boughtOutPartsOverhead: number;
	boughtOutPartsSubtotal: number;
	// --- Other ---
	businessSector?: BusinessSector;
	cadFiles: string; // JSON format, SupportingDocumentFileLineData
	clientContactId?: number;
	clientId: number;
	containerCost: number;
	crmOpportunityId?: number;
	currency: Currency;
	deliveryTerms?: Incoterms;
	engineeringAmortised: boolean;
	engineeringCost: number;
	engineeringHours: number;
	engineeringRate: number;
	grandTotal: number;
	// --- Group margin: uplift on the combined parent + child piece price ---
	groupMarginAmount: number;
	groupMarginCost: number;
	groupMarginPercentage: number;
	isFilledIn: boolean;
	leadTimeFirstOff: number;
	leadTimePhaseThree: number;
	lifetime?: number;
	lifetimeAddedValue: number;
	lifetimeCost: number;
	lifetimePrice: number;
	linkedQuotationAnalysisFormIds?: string; // JSON, number[] - other QAFs included in this QAF's client quote
	// --- Material ---
	materialOverhead: number;
	materialOverheadDisplayToCustomer: boolean;
	materialQuoting: MaterialQuoting;
	// --- Outsourced ---
	outsourcedMarginCost: number;
	outsourcedMarginPercentage: number;
	outsourcedOverhead: number;
	outsourcedOverheadDisplayToCustomer: boolean;
	outsourcedProcessesDocumentsLineData?: string; // JSON format, SupportingDocumentFileLineData
	outsourcedProcessesLineData: string; // JSON format, OutsourcedProcessesLineData
	outsourcedProcessesSubtotal: number;
	packagingAndLogistics: number;
	packagingId?: number;
	packagingTerms?: PackagingTerms;
	parentQuotationAnalysisFormId?: number;
	// --- Line data unpacked for the inline child editor: transient UI state, not persisted ---
	parsedBoughtOutParts?: BoughtOutPartsLineData[];
	parsedOutsourcedProcesses?: OutsourcedProcessLineData[];
	parsedPressProcesses?: PressProcessLineData[];
	parsedRawMaterial?: RawMaterialLineData;
	parsedToolingCosts?: ToolingCostLineData[];
	parsedToolingFixtures?: ToolingFixtureLineData[];
	partDescription: string;
	partImageUrl: string;
	partNumber: string;
	partNumberExists?: boolean; // transient UI state
	pressProcessLineData: string; // JSON format, PressProcessLineData
	pressProcessSubtotal: number;
	// --- Process margin ---
	processMarginCost: number;
	processMarginPercentage: number;
	// --- Profit ---
	profit: number;
	profitDisplayToCustomer: boolean;
	profitMarginPercentage: number;
	projectId?: number;
	projectLengthYears?: number;
	// --- Quality ---
	qualityAmortised: boolean;
	qualityCostRate: number;
	qualityHours: number;
	qualityLevelCost: number; // the quality total
	quantityBreakLineData?: string; // JSON format, QuantityBreakLineData
	// On a child: how many go into one parent. Scales the child's piece price in the parent's roll-up.
	quantityPerAssembly: number;
	// --- Rate approval: set by the API on save ---
	rateApprovalRequired: boolean;
	rateOverrides?: string; // JSON, QuotationAnalysisFormRateOverride[]
	// --- Raw material (flat — single material) ---
	rawMaterialConfirmedCost: boolean;
	rawMaterialLineData: string; // JSON format, RawMaterialLineData (single element array)
	rawMaterialMarginCost: number;
	rawMaterialMarginPercentage: number;
	rawMaterialSubtotal: number;
	rawMaterialSupplierId?: number;
	revisionLevel: string;
	supportingDocumentsLineData: string; // JSON format, SupportingDocumentFileLineData
	// --- Tooling modular ---
	toolingCostLineData: string; // JSON format, ToolingCostLineData[]
	toolingFixtureLineData: string; // JSON format, ToolingFixtureLineData
	toolingFixtureSubtotal: number;
	toolingFixtureTotalChanges: number;
	// --- Tooling margin ---
	toolingMarginCost: number;
	toolingMarginPercentage: number;
	toolingOverhead: number;
	toolingOverheadDisplayToCustomer: boolean;
	toolingTotalCost: number;
	totalFixtureCosts: number;
	totalPartCost: number; // piece price
	totalToolingCosts: number;
	unitOfMeasure: string;
	weeklyMaxCapacity: number;
	weldAndAssemblyLineData: string; // JSON format, WeldAndAssemblyLineData
	weldAndAssemblySubtotal: number;
}

export class QuotationAnalysisForm implements QuotationAnalysisForm {
	adminAmortised = true;
	adminCost = 0;
	adminHours = 0;
	adminRate = 0;
	annualCost = 0;
	annualPrice = 0;
	annualValueAdded = 0;
	batchCost = 0;
	batchPrice = 0;
	batchSize = 1;
	batchValueAdded = 0;
	boughtOutMarginCost = 0;
	boughtOutMarginPercentage = 0;
	currency = Currency.GBP;
	engineeringAmortised = true;
	engineeringCost = 0;
	engineeringHours = 0;
	engineeringRate = 0;
	groupMarginAmount = 0;
	groupMarginCost = 0;
	groupMarginPercentage = 0;
	lifetimeAddedValue = 0;
	lifetimeCost = 0;
	lifetimePrice = 0;
	materialQuoting = MaterialQuoting.Fast;
	outsourcedMarginCost = 0;
	outsourcedMarginPercentage = 0;
	processMarginCost = 0;
	processMarginPercentage = 0;
	profitMarginPercentage = 0;
	qualityAmortised = true;
	qualityCostRate = 0;
	qualityHours = 0;
	qualityLevelCost = 0;
	quantityPerAssembly = 1;
	rateApprovalRequired = false;
	rawMaterialConfirmedCost = false;
	rawMaterialMarginCost = 0;
	rawMaterialMarginPercentage = 0;
	toolingCostLineData = "[]";
	toolingFixtureSubtotal = 0;
	toolingFixtureTotalChanges = 0;
	toolingMarginCost = 0;
	toolingMarginPercentage = 0;
	toolingOverhead = 0;
	toolingOverheadDisplayToCustomer = false;
	toolingTotalCost = 0;
	totalFixtureCosts = 0;
	totalToolingCosts = 0;
	constructor(data?: Partial<QuotationAnalysisForm>) {
		if (data) {
			for (const key of Object.keys(data)) {
				(this as any)[key] = (data as any)[key];
			}
		}
	}
}
